<script lang="ts">
  import { type ComponentType } from "svelte";
  import { fade } from "svelte/transition";
  import { spring } from "svelte/motion";
  import { createMutation, useQueryClient } from "@tanstack/svelte-query";

  import { getEntityId } from "./data";
  import { linkTransactionsModal } from "../../../stores/link-transactions-modal-store";
  import {
    createGetRequestInvoiceDetailsQuery,
    createLinkingOptionsQuery,
    transactionKeys,
  } from "../../../queries/transactions";
  import {
    linkTransactions,
    type GroupedTransaction,
    type TransactionType,
    sendRequestSupplierInvoiceEmail,
  } from "../../../api/transactions";
  import { INVOICE_TYPES } from "../../../helpers/constants";
  import DocumentLinkOption from "./link-options/DocumentLinkOption.svelte";
  import LargeButton from "../../buttons/LargeButton.svelte";
  import OrderLinkOption from "./link-options/OrderLinkOption.svelte";
  import InvoiceLinkOptionSkeleton from "../../skeletons/InvoiceLinkOptionSkeleton.svelte";
  import OrderLinkOptionSkeleton from "../../skeletons/OrderLinkOptionSkeleton.svelte";
  import Icon from "../../icons/Icon.svelte";
  import MissingInvoiceOption from "./link-options/MissingInvoiceOption.svelte";
  import EmptyStateBasic from "../../empty-states/EmptyStateBasic.svelte";
  import { isValidEmail } from "../../../helpers/utils";
  import { AUTOFORWARD_INVOICES_EMAIL } from "../../../constants/email";
  import { t } from "../../../helpers/translations";
  import { selectedBuyer } from "../../../stores/buyer";

  const OPTION_TYPE_MAP: Record<TransactionType, ComponentType> = {
    invoice_arrived: DocumentLinkOption,
    manual_invoice: DocumentLinkOption,
    pro_invoice_arrived: DocumentLinkOption,
    payment: DocumentLinkOption,
    order: OrderLinkOption,
    refund: DocumentLinkOption,
    creditnote: DocumentLinkOption,
    checkin: DocumentLinkOption,
    credit_request: DocumentLinkOption,
  };

  const queryClient = useQueryClient();

  let selectedTransaction: GroupedTransaction | undefined = undefined;
  let showRequestInvoice: boolean = false;
  let invoiceRequestSuccess: boolean = false;
  let invoiceRequestEmail: string =
    $selectedBuyer?.invoice_forwarding_emails?.join(", ") ?? AUTOFORWARD_INVOICES_EMAIL;

  function selectTransaction(transaction?: GroupedTransaction) {
    selectedTransaction = transaction;
  }

  const linkTransactionMutation = createMutation({
    mutationFn: linkTransaction,
    onSuccess: () => {
      if (!transaction) return;
      queryClient.invalidateQueries({
        queryKey: transactionKeys.relatedTransactions(transaction.type, getEntityId(transaction)),
      });
      queryClient.invalidateQueries({
        queryKey: transactionKeys.groupedByBuyerId(transaction.buyer_id),
      });

      linkTransactionsModal.hideLinkingModule();
    },
  });

  async function linkTransaction() {
    if (transaction && selectedTransaction) {
      await linkTransactions(
        transaction.buyer_id,
        transaction?.type,
        getEntityId(transaction),
        selectedTransaction.type,
        getEntityId(selectedTransaction)
      );
    }
  }

  const requestInvoiceMutation = createMutation({
    mutationFn: requestInvoice,
    onError: (error) => {
      alert(error.message);
    },
    onSuccess: () => {
      if (transaction) {
        invoiceRequestSuccess = true;
        queryClient.invalidateQueries({
          queryKey: transactionKeys.requestInvoiceDetails(
            transaction.supplier_id,
            transaction.reference
          ),
        });
        queryClient.invalidateQueries({
          queryKey: ["landing"],
        });
      }

      setTimeout(() => {
        linkTransactionsModal.hideLinkingModule();
        invoiceRequestSuccess = false;
      }, 1000);
    },
  });

  async function requestInvoice() {
    if (!isValidEmail(invoiceRequestEmail)) {
      throw new Error("Please enter a valid email address");
    }

    if (transaction) {
      await sendRequestSupplierInvoiceEmail(
        transaction.buyer_id,
        transaction.supplier_id,
        transaction.reference,
        invoiceRequestEmail
      );
    }
  }

  const actionSpring = spring(8, { stiffness: 0.1, damping: 0.3 });
  $: {
    actionSpring.update((_value) => (selectedTransaction ? 0 : 8));
  }

  $: transaction = $linkTransactionsModal.primaryTransaction;
  $: entityId = getEntityId(transaction);
  $: SkeletonComponent = INVOICE_TYPES.includes(transaction?.type ?? "")
    ? OrderLinkOptionSkeleton
    : InvoiceLinkOptionSkeleton;

  $: linkingOptionsQuery = transaction && createLinkingOptionsQuery(transaction.type, entityId);

  $: requestInvoiceDetailsQuery =
    transaction &&
    createGetRequestInvoiceDetailsQuery(
      transaction.type,
      transaction.supplier_id,
      transaction.reference
    );

  $: noLinkingOptions = $linkingOptionsQuery?.data?.length === 0;
  $: requestDetails = $requestInvoiceDetailsQuery?.data?.request_details;

  $: showRequestInvoiceCta =
    (transaction?.type === "order" && noLinkingOptions && !requestDetails) || showRequestInvoice;
</script>

<div class="container" class:container-wide={transaction?.type !== "order"}>
  <div class="options">
    {#if $linkingOptionsQuery && $linkingOptionsQuery.data}
      {#if transaction?.type === "order"}
        <div class="linking-option" class:full-height={noLinkingOptions && !requestDetails}>
          <MissingInvoiceOption
            emails={$requestInvoiceDetailsQuery?.data?.supplier_emails || []}
            requestDetails={$requestInvoiceDetailsQuery?.data?.request_details}
            orderNumber={transaction?.po_number ?? ""}
            bind:emailTarget={invoiceRequestEmail}
            bind:primaryOption={noLinkingOptions}
            bind:expanded={showRequestInvoice}
          />
        </div>
      {/if}
      {#if $linkingOptionsQuery.data.length === 0 && transaction?.type !== "order"}
        <EmptyStateBasic title="no documents found" subtitle="">
          <Icon
            slot="illustration"
            name="listLarge"
            --w="120px"
            --h="120px"
            --color="var(--rk-neutral-2)"
          />
        </EmptyStateBasic>
      {:else}
        {#each $linkingOptionsQuery.data as linkingOption}
          <div class="linking-option">
            <svelte:component
              this={OPTION_TYPE_MAP[linkingOption.type]}
              transaction={linkingOption}
              didSelect={selectTransaction}
              selected={selectedTransaction?.key === linkingOption.key}
            />
          </div>
        {/each}
      {/if}
    {:else}
      {#each Array(3) as _}
        <div class="linking-option">
          <svelte:component this={SkeletonComponent} />
        </div>
      {/each}
    {/if}
  </div>

  {#if selectedTransaction || showRequestInvoiceCta}
    <div
      class="floating-button"
      class:floating-button-gradient={noLinkingOptions && !requestDetails}
      transition:fade|global={{ duration: 150 }}
    >
      {#if showRequestInvoiceCta}
        <LargeButton
          title={invoiceRequestSuccess ? t("global.done.cta") : t("requestInvoice.cta.title")}
          square
          loading={$requestInvoiceMutation.isPending}
          onClick={() => $requestInvoiceMutation.mutate()}
        />
      {:else if selectedTransaction}
        <LargeButton
          title={t("global.done.cta")}
          square
          loading={$linkTransactionMutation.isPending}
          onClick={() => $linkTransactionMutation.mutate()}
        />
      {/if}
    </div>
  {/if}
</div>

<style>
  .container {
    display: flex;
    position: relative;
    flex: 1;
    flex-direction: column;
    width: 480px;
    height: 100%;
    background-color: var(--rk-divider-0);
    padding: 0 16px 0 16px;
    border-radius: 16px;
  }

  .container-wide {
    width: 620px;
  }

  @media screen and (min-width: 1440px) {
    .container {
      width: 560px;
    }

    .container-wide {
      width: 720px;
    }
  }

  .options {
    height: 100%;
    padding-top: 16px;
    padding-bottom: 94px;
    overflow-y: scroll;
  }

  .linking-option {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    border-radius: 8px;
    margin-bottom: 8px;
    overflow: hidden;
  }

  .linking-option.full-height {
    height: 99%;
    margin-bottom: 8px;
  }

  .floating-button {
    position: absolute;
    display: flex;
    padding: 32px 16px 16px 16px;
    border-bottom-left-radius: 24px;
    border-bottom-right-radius: 24px;
    justify-content: center;
    align-items: center;
    bottom: 0;
    right: 0;
    left: 0;
  }

  .floating-button-gradient {
    background: linear-gradient(180deg, rgba(235, 235, 235, 0) 0%, #ebebeb 30%);
  }
</style>
